import {Component, DestroyRef, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {Subject, Subscription} from 'rxjs';
import {ObjektModels} from '../../../models/objekt-models';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import ObjectListViewItem = ObjektModels.ObjectListViewItem;

@Component({
    selector: 'app-object-listview', templateUrl: './object-listview.component.html', styleUrls: ['./object-listview.component.scss'],
})
export class ObjectListViewComponent implements OnInit, OnDestroy {
    @Output() objectSelected = new EventEmitter<any>();
    @Input() data?: Subject<{ data: ObjectListViewItem[], online: boolean }>;
    @Input() selectObjectEventEmitter?: EventEmitter<string>;
    @Input() categoryMode: boolean = true;
    @ViewChild('root', {static: true}) treeRoot?: ElementRef<HTMLDivElement>;
    private treeViewData: NodeObjectData[] = [];
    private categories: Map<string, Category> = new Map();
    private data$: Subscription | null = null;
    private destroyRef = inject(DestroyRef);

    constructor() {
    }

    ngOnDestroy(): void {
        this.data$?.unsubscribe();
        this.selectObjectEventEmitter?.complete();
        this.selectObjectEventEmitter?.unsubscribe();
    }

    ngOnInit(): void {
        this.data$ = this.data!.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async data => {
            this.treeViewData = data.data;

            if (this.treeRoot) {
                this.treeRoot.nativeElement.innerHTML = '<div class="node"></div><div class="children"></div>';
            }

            for (const nodeObjectData of data.data) {
                this.insertNode(nodeObjectData);
            }
        });

        this.selectObjectEventEmitter?.subscribe(id => {
            const node = this.findTreeNodeByObjectId(id);
            this.treeRoot?.nativeElement.querySelector('.selected')?.classList.remove('selected');
            node?.children[0].classList.add('selected');
            this.scrollAndOpenNodes(node, node);
        });
    }

    private scrollAndOpenNodes(node, base) {
        if (node?.parentNode) {
            node?.parentElement?.classList.remove('closed');
            setTimeout(() => this.scrollAndOpenNodes(node.parentElement.parentElement, base), 20);
        } else {
            setTimeout(() => base?.scrollIntoView({behavior: 'instant', block: 'center'}), 20);
        }
    }

    public insertNode(nodeObjectData) {
        if (this.findTreeNodeByObjectId(nodeObjectData.objectID)) {
            return; //already in tree
        }

        if (this.categoryMode) {
            if (nodeObjectData.depth == 0) {
                const cat = this.getCategory(nodeObjectData.objectTypeDesc);
                const node = this.createNode(nodeObjectData);
                cat.amount = cat.amount + 1;
                cat.element.innerText = cat.name + ' | ' + cat.amount;
                cat.childContainer.appendChild(node);
            } else {
                //data is already comes sorted by depth so we don't worry about the parent node not being here yet
                const parent = this.findTreeNodeByObjectId(nodeObjectData.parentID);
                const node = this.createNode(nodeObjectData);
                parent?.children[0].children[0].classList.remove('none');
                parent?.children[1].appendChild(node);
            }
        } else {
            const node = this.createNode(nodeObjectData);
            const parent = this.findTreeNodeByObjectId(nodeObjectData.parentID);
            if (parent) {
                parent?.children[0].children[0].classList.remove('none');
                parent?.children[1].appendChild(node);
            } else {
                this.treeRoot?.nativeElement.appendChild(node);
            }
        }
    }

    public findTreeNodeByObjectId(objectId: string): HTMLElement | null {
        return this.treeRoot!.nativeElement.querySelector('[data-objectid="' + objectId + '"]');
    }

    private getCategory(category: string): Category {
        let cat = this.categories.get(category);
        if (!cat) {
            // need to create it
            const catElement = document.createElement('div');
            catElement.classList.add('category-container');

            const element = document.createElement('div');
            element.classList.add('node', 'category');
            catElement.appendChild(element);

            const children = document.createElement('div');
            children.classList.add('children');
            catElement.appendChild(children);

            cat = {
                amount: 0,
                name: category,
                element: element,
                icon: '',
                childContainer: children
            };
            this.categories.set(category, cat);

            cat.element.innerText = cat.name + ' | ' + cat.amount;
            this.treeRoot?.nativeElement.appendChild(catElement);
            return cat;
        } else {
            return cat;
        }
    }

    private createNode(data) {
        const node = document.createElement('div');
        node.classList.add('node');
        node.dataset.objectid = data.objectID;

        const parent = document.createElement('div');
        parent.classList.add('parent');

        const expand = document.createElement('div');
        expand.classList.add('expand', 'none');
        expand.onclick = (ev) => {
            if (expand.classList.contains('open')) {
                children.classList.add('closed');
                expand.classList.remove('open');
            } else {
                children.classList.remove('closed');
                expand.classList.add('open');
            }

        };
        parent.appendChild(expand);

        const img = document.createElement('div');
        img.style.backgroundImage = 'url(' + data.imageURL + ')';
        img.classList.add('icon');
        parent.appendChild(img);
        img.onclick = (ev) => {
            this.treeRoot?.nativeElement.querySelector('.selected')?.classList.remove('selected');
            parent.classList.add('selected');
            this.objectSelected?.next(data.objectID);
        };

        const nodeData = document.createElement('div');
        nodeData.classList.add('data');
        nodeData.onclick = (ev) => {
            this.treeRoot?.nativeElement.querySelector('.selected')?.classList.remove('selected');
            parent.classList.add('selected');
            this.objectSelected?.next(data.objectID);
        };

        const title = document.createElement('div');
        title.innerText = data.objectNr;
        title.classList.add('title');
        nodeData.appendChild(title);

        const desc = document.createElement('div');
        desc.innerText = data.description;
        desc.classList.add('desc');
        nodeData.appendChild(desc);

        const note4 = document.createElement('div');
        note4.innerText = data.note4;
        note4.classList.add('note4');
        nodeData.appendChild(note4);

        parent.appendChild(nodeData);

        const children = document.createElement('div');
        children.classList.add('children-container', 'closed');

        node.appendChild(parent);
        node.appendChild(children);

        return node;
    }
}

export interface NodeObjectData {
    description?: string;
    objectID?: string;
    parentID?: string;
    depth: number;
    objectTypeDesc: string;
    note4?: string;
    objectNr?: string;
    objectType?: number;
    objectTypeImg?: string;
}

export interface Category {
    amount: number;
    name: string;
    icon: string;
    element: HTMLElement;
    childContainer: HTMLElement;
}


